import type {
  CustomizationChangedField,
  CustomizationDemandDraft,
  CustomizationDemandDisplayField,
  CustomizationDemandDisplayGroup,
  CustomizationOpportunityLinkedProjectItem,
  CustomizationProjectListFilters,
  CustomizationProjectListItem,
  CustomizationProjectSnapshot,
  CustomizationServiceStandardDraft,
} from "../types";
import {
  customizationDemandFieldLabels,
  customizationDemandGroupLabels,
  customizationDemandLookupValueLabels,
} from "../config";

export type DemandFieldKind = "text" | "tag" | "number" | "datetime" | "summary";

/** 后端明确标记缺项时禁止复制；缺字段仅用于兼容前后端滚动发布。 */
export function isOpportunityLinkedProjectCopyable(
  item: Pick<CustomizationOpportunityLinkedProjectItem, "copyable">,
): boolean {
  return item.copyable !== false;
}

/** 窄屏增定制 Tab 键：本商机已有 / 新增 / 历史。 */
export type OpportunityAddCustomizationTabKey = "linked" | "create" | "history";

/**
 * 窄屏增定制 Tab 顺序：本商机有定制时「本商机已有定制」第一，否则「新增定制」第一；历史定制始终第三。
 */
export function resolveOpportunityAddCustomizationTabOrder(
  hasLinkedProjects: boolean,
): OpportunityAddCustomizationTabKey[] {
  if (hasLinkedProjects) {
    return ["linked", "create", "history"];
  }
  return ["create", "linked", "history"];
}

/** 窄屏增定制默认 Tab：有本商机定制时打开「本商机已有定制」，否则打开「新增定制」。 */
export function resolveOpportunityAddCustomizationDefaultTab(
  hasLinkedProjects: boolean,
): OpportunityAddCustomizationTabKey {
  return hasLinkedProjects ? "linked" : "create";
}

export type DemandChangeDisplay = {
  key: string;
  label: string;
  previous: string;
  current: string;
};

export type DemandRequiredStatus = {
  missingFields: string[];
  reviewReasons: string[];
  status: "pending" | "review" | "missing" | "complete";
  title: string;
  text: string;
};

export type CustomizationProjectListStatus = {
  label: "草稿" | "设计中" | "已发布" | "已确认";
  color: string;
};

type NestedChangedFieldAlias = {
  fieldKey: string;
  valueKeys: string[];
};

/** 规整定制项目列表筛选条件。 */
export function normalizeCustomizationProjectListFilters(
  filters: CustomizationProjectListFilters,
): CustomizationProjectListFilters {
  const normalized: CustomizationProjectListFilters = {
    opportunityId: filters.opportunityId,
    keyword: normalizeOptionalText(filters.keyword),
    organizationName: normalizeOptionalText(filters.organizationName),
    destination: normalizeOptionalText(filters.destination),
    dealProgress: normalizeOptionalText(filters.dealProgress),
    opportunityGroupSource: normalizeOptionalText(filters.opportunityGroupSource),
    tripType: normalizeOptionalText(filters.tripType),
    signType: normalizeOptionalText(filters.signType),
    dispatcherUserId: normalizeOptionalText(filters.dispatcherUserId),
    operatingCompany: normalizeOptionalText(filters.operatingCompany),
    hasItinerary: filters.hasItinerary,
    hasOrder: filters.hasOrder,
    hasOpportunity: filters.hasOpportunity,
  };
  return Object.fromEntries(
    Object.entries(normalized).filter(([, value]) => value !== undefined),
  ) as CustomizationProjectListFilters;
}

/** 获取列表当前状态：新系统未单独落这四态，列表按成单进度映射展示，项目状态/阶段仅兜底。 */
export function resolveCustomizationProjectListStatus(
  item: Pick<CustomizationProjectListItem, "status" | "stage" | "dealProgress">,
): CustomizationProjectListStatus {
  if (item.dealProgress === "赢单") {
    return { label: "已确认", color: "green" };
  }
  if (item.dealProgress === "80%") {
    return { label: "已发布", color: "green" };
  }
  if (item.dealProgress === "50%") {
    return { label: "设计中", color: "arcoblue" };
  }
  if (item.dealProgress === "输单") {
    return { label: "草稿", color: "gray" };
  }
  if (item.status === "confirmed" || item.stage === "contracting") {
    return { label: "已确认", color: "green" };
  }
  if (item.status === "quoted" || item.stage === "customer_confirming") {
    return { label: "已发布", color: "green" };
  }
  if (item.status === "draft") {
    return { label: "草稿", color: "gold" };
  }
  return { label: "设计中", color: "arcoblue" };
}

/** 判断列表项目是否已到订单转换状态。 */
export function isCustomizationProjectListStatusConfirmed(
  item: Pick<CustomizationProjectListItem, "status" | "stage" | "dealProgress">,
): boolean {
  return resolveCustomizationProjectListStatus(item).label === "已确认";
}

/** 根据当前字段生成商机名称预览，后端保存后仍是最终权威。 */
export function buildCustomizationProjectNamePreview(
  project: CustomizationProjectSnapshot | null,
  demand?: CustomizationDemandDraft | null,
  patch: Partial<CustomizationProjectSnapshot> = {},
): string {
  const mergedProject = project ? { ...project, ...patch } : patch;
  const studentCount = demand?.studentCount ?? demand?.summary?.studentCount;
  const teacherCount = demand?.teacherCount ?? demand?.summary?.teacherCount;
  const totalCount = demand?.totalCount ?? demand?.summary?.totalCount ?? sumCounts(studentCount, teacherCount);
  const parts = [
    mergedProject.primarySchoolNameSnapshot,
    mergedProject.customerName,
    mergedProject.tripType,
    demand?.departureStartDate ?? demand?.summary?.departureStartDate,
    totalCount ? `${totalCount}人` : undefined,
  ];
  const name = parts.map((item) => normalizeOptionalText(item)).filter(Boolean).join("-");
  return name || mergedProject.legacyId || mergedProject.projectCode || mergedProject.name || "";
}

const requiredDemandFieldKeys = new Set([
  "primarySchoolNameSnapshot",
  "customerName",
  "contactName",
  "customerType",
  "tripType",
  "signType",
  "dispatcherUserName",
  "operatingCompany",
  "departureStartDate",
  "durationDays",
  "studentCount",
  "teacherCount",
  "schoolPickupRequirement",
  "invoiceRequirement",
  "teacherMealRequirement",
  "teacherTicketRequirement",
  "teacherTransportRequirement",
  "teacherAccommodationRequirement",
  "majorNames",
  "roundTripTransportNote",
  "guideCount",
]);

const destinationRequiredFieldLabel = "目的地信息";
const destinationRequiredFieldKeys = new Set(["provinces", "pointNames", "stayAreaNames"]);
const destinationRequiredFieldLabels = new Set([
  customizationDemandFieldLabels.provinces,
  customizationDemandFieldLabels.pointNames,
  customizationDemandFieldLabels.stayAreaNames,
]);
const requiredDemandFieldLabels = new Set([
  ...[...requiredDemandFieldKeys]
    .map((key) => customizationDemandFieldLabels[key])
    .filter((label): label is string => Boolean(label)),
  "出发高校",
  destinationRequiredFieldLabel,
  ...destinationRequiredFieldLabels,
]);

const demandChangedFieldAliases: Record<string, string> = {
  sourceTraceSummary: "sourceTraceSummary",
  demandSummary: "demandSummary",
  destinationSummary: "destinationSummary",
  durationDays: "durationDays",
  travelTime: "travelTime",
  departureStartDate: "departureStartDate",
  totalCount: "totalCount",
  studentCount: "studentCount",
  teacherCount: "teacherCount",
  budgetPerPerson: "budgetPerPerson",
  teachingRequirements: "teachingRequirements",
  specialRequirements: "specialRequirements",
  schoolPickupRequirement: "schoolPickupRequirement",
  invoiceRequirement: "invoiceRequirement",
  teacherMealRequirement: "teacherMealRequirement",
  teacherTicketRequirement: "teacherTicketRequirement",
  teacherTransportRequirement: "teacherTransportRequirement",
  teacherAccommodationRequirement: "teacherAccommodationRequirement",
  remark: "remark",
};

const relationChangedFieldAliases: Record<string, NestedChangedFieldAlias> = {
  provinces: { fieldKey: "provinces", valueKeys: ["provinces", "province_names"] },
  majorNames: { fieldKey: "majorNames", valueKeys: ["majorNames", "major_names"] },
  pointNames: { fieldKey: "pointNames", valueKeys: ["pointNames", "point_names"] },
  stayAreaNames: { fieldKey: "stayAreaNames", valueKeys: ["stayAreaNames", "stay_area_names"] },
};
const relationDisplayFieldKeys = new Set(["provinces", "majorNames", "pointNames", "stayAreaNames"]);

const serviceStandardChangedFieldAliases: Record<string, NestedChangedFieldAlias> = {
  roundTripTransportNote: {
    fieldKey: "roundTripTransportNote",
    valueKeys: ["roundTripTransportNote", "round_trip_transport_note"],
  },
  localTransportNote: {
    fieldKey: "localTransportNote",
    valueKeys: ["localTransportNote", "local_transport_note"],
  },
  accommodationNote: {
    fieldKey: "accommodationNote",
    valueKeys: ["accommodationNote", "accommodation_note"],
  },
  guideCount: { fieldKey: "guideCount", valueKeys: ["guideCount", "guide_count"] },
  guideNote: { fieldKey: "guideNote", valueKeys: ["guideNote", "guide_note"] },
  includeTickets: { fieldKey: "includeTickets", valueKeys: ["includeTickets", "include_tickets"] },
  ticketNote: { fieldKey: "ticketNote", valueKeys: ["ticketNote", "ticket_note"] },
  includeMeals: { fieldKey: "includeMeals", valueKeys: ["includeMeals", "include_meals"] },
  mealStandards: { fieldKey: "mealStandards", valueKeys: ["mealStandards", "meal_standards"] },
  mealNote: { fieldKey: "mealNote", valueKeys: ["mealNote", "meal_note"] },
  companyVisitCount: {
    fieldKey: "companyVisitCount",
    valueKeys: ["companyVisitCount", "company_visit_count"],
  },
  companyVisitNote: {
    fieldKey: "companyVisitNote",
    valueKeys: ["companyVisitNote", "company_visit_note"],
  },
  intangibleHeritageCount: {
    fieldKey: "intangibleHeritageCount",
    valueKeys: ["intangibleHeritageCount", "intangible_heritage_count"],
  },
  intangibleHeritageNote: {
    fieldKey: "intangibleHeritageNote",
    valueKeys: ["intangibleHeritageNote", "intangible_heritage_note"],
  },
};

/** 生成定制需求分组展示数据。 */
export function buildDemandDisplayGroups(
  demand: CustomizationDemandDraft | null,
  project: CustomizationProjectSnapshot | null,
): CustomizationDemandDisplayGroup[] {
  const fallbackGroups = reorderDemandDisplayGroups(buildFallbackDemandDisplayGroups(demand, project));
  if (demand?.displayGroups?.length) {
    return markChangedFields(
      reorderDemandDisplayGroups(
        mergeDemandDisplayGroups(normalizeDemandDisplayGroups(demand.displayGroups), fallbackGroups),
      ),
      demand,
    );
  }

  return markChangedFields(fallbackGroups, demand);
}

/** 社会类定制枚举值。 */
export const SOCIAL_CUSTOMER_TYPE = "社会类定制";

/** 社会类定制的客户字段为选填。 */
export function isSocialCustomization(customerType?: string | null): boolean {
  return customerType?.trim() === SOCIAL_CUSTOMER_TYPE;
}

/** 判断需求字段是否为必填项。 */
export function isRequiredDemandField(
  fieldKey: string,
  context?: { customerType?: string | null },
): boolean {
  if (fieldKey === "customerName" && isSocialCustomization(context?.customerType)) {
    return false;
  }
  return requiredDemandFieldKeys.has(fieldKey);
}

/** 判断需求字段是否属于目的地组合必填。 */
export function isDestinationDemandField(fieldKey: string): boolean {
  return destinationRequiredFieldKeys.has(fieldKey);
}

/** 按当前项目和需求真实字段值重新计算必填缺项，避免沿用旧版本缺项原因。 */
export function getActualDemandRequiredMissingFields(
  demand: CustomizationDemandDraft,
  project: CustomizationProjectSnapshot,
): string[] {
  const customerRequirements = demand.customerRequirements;
  const teacherRequirements = demand.teacherRequirements;
  const summary = demand.summary;
  const serviceStandard = demand.serviceStandard;
  const checks: Array<{ label: string; value: unknown }> = [
    {
      label: customizationDemandFieldLabels.primarySchoolNameSnapshot,
      value: project.primarySchoolNameSnapshot,
    },
    ...(isSocialCustomization(project.customerType)
      ? []
      : [{ label: customizationDemandFieldLabels.customerName, value: project.customerName }]),
    { label: customizationDemandFieldLabels.contactName, value: project.contactName },
    { label: customizationDemandFieldLabels.customerType, value: project.customerType },
    { label: customizationDemandFieldLabels.tripType, value: project.tripType },
    { label: customizationDemandFieldLabels.signType, value: project.signType },
    {
      label: customizationDemandFieldLabels.dispatcherUserName,
      value: project.dispatcherUserName,
    },
    {
      label: customizationDemandFieldLabels.operatingCompany,
      value: project.operatingCompany,
    },
    {
      label: customizationDemandFieldLabels.departureStartDate,
      value: demand.departureStartDate ?? summary?.departureStartDate,
    },
    {
      label: customizationDemandFieldLabels.durationDays,
      value: demand.durationDays ?? summary?.durationDays,
    },
    {
      label: customizationDemandFieldLabels.studentCount,
      value: demand.studentCount ?? summary?.studentCount,
    },
    {
      label: customizationDemandFieldLabels.teacherCount,
      value: demand.teacherCount ?? summary?.teacherCount,
    },
    {
      label: customizationDemandFieldLabels.schoolPickupRequirement,
      value:
        demand.schoolPickupRequirement ?? customerRequirements?.schoolPickupRequirement,
    },
    {
      label: customizationDemandFieldLabels.invoiceRequirement,
      value: demand.invoiceRequirement ?? customerRequirements?.invoiceRequirement,
    },
    {
      label: customizationDemandFieldLabels.teacherMealRequirement,
      value: demand.teacherMealRequirement ?? teacherRequirements?.teacherMealRequirement,
    },
    {
      label: customizationDemandFieldLabels.teacherTicketRequirement,
      value: demand.teacherTicketRequirement ?? teacherRequirements?.teacherTicketRequirement,
    },
    {
      label: customizationDemandFieldLabels.teacherTransportRequirement,
      value:
        demand.teacherTransportRequirement ?? teacherRequirements?.teacherTransportRequirement,
    },
    {
      label: customizationDemandFieldLabels.teacherAccommodationRequirement,
      value:
        demand.teacherAccommodationRequirement ??
        teacherRequirements?.teacherAccommodationRequirement,
    },
    { label: customizationDemandFieldLabels.majorNames, value: demand.relations?.majorNames },
    { label: customizationDemandFieldLabels.guideCount, value: serviceStandard?.guideCount },
    {
      label: customizationDemandFieldLabels.roundTripTransportNote,
      value: serviceStandard?.roundTripTransportNote,
    },
  ];
  const missingFields = checks
    .filter((item) => !hasConcreteValue(item.value))
    .map((item) => item.label);
  if (!hasDemandDestinationValue(demand)) {
    missingFields.push(destinationRequiredFieldLabel);
  }
  return missingFields;
}

/** 生成变更字段展示数据。 */
export function buildDemandChangeList(demand: CustomizationDemandDraft | null): DemandChangeDisplay[] {
  if (!demand?.changedFields) {
    return [];
  }
  return Object.entries(normalizeChangedFieldsForDisplay(demand.changedFields))
    .map(([key, change]) => ({
      key,
      label: customizationDemandFieldLabels[key] ?? key,
      previous: formatChangeValue(change.previous, key),
      current: formatChangeValue(change.current, key),
      hasValue: hasMeaningfulChange(change.previous, change.current),
    }))
    .filter((item) => item.hasValue);
}

/** 变更字段数量。 */
export function countChangedDemandFields(demand: CustomizationDemandDraft | null): number {
  return buildDemandChangeList(demand).length;
}

/** 获取必填项状态展示数据。 */
export function getDemandRequiredStatus(demand: CustomizationDemandDraft | null): DemandRequiredStatus {
  if (!demand) {
    return {
      missingFields: [],
      reviewReasons: [],
      status: "pending",
      title: "待生成定制需求",
      text: "当前还没有结构化需求版本。请先勾选需求追溯记录，生成第一版后再校验必填项。",
    };
  }

  const missingFields = normalizeDemandMissingFields(demand?.needsHumanReviewReasons).filter(
    (field) => field !== destinationRequiredFieldLabel || !hasDemandDestinationValue(demand),
  );
  const reviewReasons = normalizeDemandReviewReasons(demand?.needsHumanReviewReasons);
  if (reviewReasons.length > 0) {
    return {
      missingFields,
      reviewReasons,
      status: "review",
      title: "必填项待确认",
      text:
        missingFields.length > 0
          ? `必填项待确认：${reviewReasons.join("、")}；待补字段：${missingFields.join("、")}`
          : `必填项待确认：${reviewReasons.join("、")}`,
    };
  }
  if (missingFields.length > 0) {
    return {
      missingFields,
      reviewReasons,
      status: "missing",
      title: "必填项状态",
      text: `缺少必填项：${missingFields.join("、")}`,
    };
  }
  return {
    missingFields,
    reviewReasons,
    status: "complete",
    title: "必填项状态",
    text: "必填项完整：当前需求已覆盖项目、目的地信息、专业、日期/天数/人数、接送/发票/老师要求、领队/导游和行程大交通工具等信息。",
  };
}

/** 规整后端返回的必填缺失字段，保持与页面状态展示一致。 */
export function normalizeDemandMissingFields(values: string[] | undefined): string[] {
  const missingFields: string[] = [];
  let hasDestinationMissing = false;

  for (const rawField of dedupeStrings(values ?? [])) {
    const field = normalizeRequiredMissingFieldLabel(rawField);
    if (isDerivedMissingDemandField(field) || !isRequiredMissingDemandField(field)) {
      continue;
    }
    if (isDestinationRequiredMissingField(field)) {
      if (!hasDestinationMissing) {
        missingFields.push(destinationRequiredFieldLabel);
        hasDestinationMissing = true;
      }
      continue;
    }
    missingFields.push(field);
  }

  return missingFields;
}

/** 规整后端返回的业务复核原因，与真实必填字段缺失分开展示。 */
export function normalizeDemandReviewReasons(values: string[] | undefined): string[] {
  return dedupeStrings(values ?? []).filter(
    (field) => {
      const normalizedField = normalizeRequiredMissingFieldLabel(field);
      return (
        !isDerivedMissingDemandField(normalizedField) &&
        !isRequiredMissingDemandField(normalizedField)
      );
    },
  );
}

/** 去除缺项原因前缀，统一后端裸标签和本地草稿提示的分类口径。 */
function normalizeRequiredMissingFieldLabel(field: string): string {
  return field
    .trim()
    .replace(/^(?:缺少必填项|必填项待确认|缺少|待补|请补充|请填写)[：:\s]*/, "")
    .trim();
}

/** 将后端 changedFields 映射到字段自身，供页面直接标红变更项。 */
function markChangedFields(
  groups: CustomizationDemandDisplayGroup[],
  demand: CustomizationDemandDraft | null,
): CustomizationDemandDisplayGroup[] {
  const changedFields = normalizeChangedFieldsForDisplay(demand?.changedFields);
  return groups.map((group) => ({
    ...group,
    fields: group.fields.map((fieldItem) => {
      const change = changedFields[fieldItem.key];
      const changed = Boolean(fieldItem.changed || change);
      const changedDisplayText =
        changed &&
        change &&
        hasConcreteValue(change.current) &&
        !hasStructuredRelationValue(demand, fieldItem.key)
          ? formatChangeValue(change.current, fieldItem.key)
          : undefined;

      return {
        ...fieldItem,
        value: changedDisplayText ?? fieldItem.value,
        displayText: changedDisplayText ?? fieldItem.displayText,
        changed,
      };
    }),
  }));
}

/** 将后端多种 changedFields key 形态统一成页面字段 key。 */
function normalizeChangedFieldsForDisplay(
  changedFields: Record<string, CustomizationChangedField> | undefined,
): Record<string, CustomizationChangedField> {
  if (!changedFields) {
    return {};
  }
  const result: Record<string, CustomizationChangedField> = {};
  for (const [rawKey, change] of Object.entries(changedFields)) {
    const normalizedKey = normalizeChangedFieldKey(rawKey);
    if (normalizedKey === "relations") {
      mergeNestedChangedFields(result, change, relationChangedFieldAliases);
      continue;
    }
    if (normalizedKey === "serviceStandard") {
      mergeNestedChangedFields(result, change, serviceStandardChangedFieldAliases);
      continue;
    }
    if (normalizedKey === "demand") {
      mergeFlatNestedChangedFields(result, change, demandChangedFieldAliases);
      continue;
    }
    result[normalizedKey] = change;
  }
  return result;
}

/** 将单个 changedFields key 规整为页面字段 key。 */
function normalizeChangedFieldKey(key: string): string {
  const text = key.trim();
  if (!text) {
    return text;
  }
  const parts = text.split(".").map((part) => toCamelCase(part));
  const firstPart = parts[0];
  const lastPart = parts[parts.length - 1] ?? "";
  if (
    firstPart === "summary" ||
    firstPart === "customerRequirements" ||
    firstPart === "teacherRequirements" ||
    firstPart === "demand"
  ) {
    return demandChangedFieldAliases[lastPart] ?? (firstPart === "demand" ? "demand" : lastPart);
  }
  if (firstPart === "relations") {
    return relationChangedFieldAliases[lastPart]?.fieldKey ?? "relations";
  }
  if (firstPart === "serviceStandard") {
    return serviceStandardChangedFieldAliases[lastPart]?.fieldKey ?? "serviceStandard";
  }

  const flattenedKey = toCamelCase(text.replace(/\./g, "_"));
  const relationKey = relationChangedFieldAliases[flattenedKey]?.fieldKey;
  if (relationKey) {
    return relationKey;
  }
  const serviceStandardKey = serviceStandardChangedFieldAliases[flattenedKey]?.fieldKey;
  if (serviceStandardKey) {
    return serviceStandardKey;
  }
  const demandKey = demandChangedFieldAliases[flattenedKey];
  if (demandKey) {
    return demandKey;
  }
  return flattenedKey;
}

/** 将 demand 整包差异拆成页面字段差异。 */
function mergeFlatNestedChangedFields(
  result: Record<string, CustomizationChangedField>,
  change: CustomizationChangedField,
  aliases: Record<string, string>,
): void {
  const previous = asRecord(change.previous);
  const current = asRecord(change.current);
  if (!previous && !current) {
    return;
  }
  for (const [valueKey, fieldKey] of Object.entries(aliases)) {
    const previousValue = previous?.[valueKey];
    const currentValue = current?.[valueKey];
    if (!hasMeaningfulChange(previousValue, currentValue)) {
      continue;
    }
    result[fieldKey] = {
      ...change,
      previous: previousValue,
      current: currentValue,
    };
  }
}

/** 将 relations 或 serviceStandard 整包差异拆成页面字段差异。 */
function mergeNestedChangedFields(
  result: Record<string, CustomizationChangedField>,
  change: CustomizationChangedField,
  aliases: Record<string, NestedChangedFieldAlias>,
): void {
  const previous = asRecord(change.previous);
  const current = asRecord(change.current);
  if (!previous && !current) {
    return;
  }
  for (const alias of Object.values(aliases)) {
    const previousValue = previous ? findRecordValue(previous, alias.valueKeys) : undefined;
    const currentValue = current ? findRecordValue(current, alias.valueKeys) : undefined;
    if (!hasMeaningfulChange(previousValue, currentValue)) {
      continue;
    }
    result[alias.fieldKey] = {
      ...change,
      previous: previousValue,
      current: currentValue,
    };
  }
}

/** 按候选 key 从对象里读取第一个存在的值。 */
function findRecordValue(record: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (key in record) {
      return record[key];
    }
  }
  return undefined;
}

/** 将未知对象安全收窄为普通记录。 */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

/** 将 snake_case 字段名转换成 camelCase。 */
function toCamelCase(value: string): string {
  return value.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

/** 去重并清理字符串数组，保持后端返回顺序。 */
function dedupeStrings(values: string[]): string[] {
  const result: string[] = [];
  for (const value of values) {
    const text = value.trim();
    if (text && !result.includes(text)) {
      result.push(text);
    }
  }
  return result;
}

/** 获取版本状态文本。 */
export function getDemandVersionStatus(
  demand: CustomizationDemandDraft | null,
  currentDemandId?: string,
): string {
  if (!demand?.versionNumber) {
    return "未生成";
  }
  if (demand.id && currentDemandId && demand.id === currentDemandId) {
    return "当前";
  }
  return "历史";
}

/** 获取更新时间文本。 */
export function getDemandUpdatedAt(demand: CustomizationDemandDraft | null): string {
  return demand?.updatedAt?.trim() || demand?.createdAt?.trim() || "--";
}

/** 获取摘要文本。 */
export function getDemandSummaryText(demand: CustomizationDemandDraft | null): string {
  return (
    demand?.summary?.demandSummary?.trim() ||
    demand?.demandSummary?.trim() ||
    "尚未生成定制需求"
  );
}

/** 获取追溯摘要文本。 */
export function getDemandSourceSummary(demand: CustomizationDemandDraft | null): string {
  return demand?.summary?.sourceTraceSummary?.trim() || demand?.sourceTraceSummary?.trim() || "暂无追溯摘要";
}

/** 生成后端未返回扩展分组时的兼容展示结构。 */
function buildFallbackDemandDisplayGroups(
  demand: CustomizationDemandDraft | null,
  project: CustomizationProjectSnapshot | null,
): CustomizationDemandDisplayGroup[] {
  const summary = demand?.summary;
  const customerRequirements = demand?.customerRequirements;
  const teacherRequirements = demand?.teacherRequirements;
  const serviceStandard = demand?.serviceStandard;
  const relations = demand?.relations;

  return [
    {
      key: "base",
      title: customizationDemandGroupLabels.base,
      fields: buildFields([
        field("primarySchoolNameSnapshot", customizationDemandFieldLabels.primarySchoolNameSnapshot, "text", project?.primarySchoolNameSnapshot),
        field("customerName", customizationDemandFieldLabels.customerName, "text", project?.customerName),
        field("contactName", customizationDemandFieldLabels.contactName, "text", project?.contactName),
        field("majorNames", customizationDemandFieldLabels.majorNames, "tag", relations?.majorNames),
        field("departureStartDate", customizationDemandFieldLabels.departureStartDate, "datetime", summary?.departureStartDate ?? demand?.departureStartDate),
        field("durationDays", customizationDemandFieldLabels.durationDays, "number", summary?.durationDays ?? demand?.durationDays),
        field("studentCount", customizationDemandFieldLabels.studentCount, "number", summary?.studentCount ?? demand?.studentCount),
        field("teacherCount", customizationDemandFieldLabels.teacherCount, "number", summary?.teacherCount ?? demand?.teacherCount),
        field("destinationSummary", customizationDemandFieldLabels.destinationSummary, "text", summary?.destinationSummary ?? demand?.destinationSummary),
        field("provinces", customizationDemandFieldLabels.provinces, "tag", relations?.provinces),
        field("stayAreaNames", customizationDemandFieldLabels.stayAreaNames, "tag", relations?.stayAreaNames),
        field("pointNames", customizationDemandFieldLabels.pointNames, "tag", relations?.pointNames),
        field("budgetPerPerson", customizationDemandFieldLabels.budgetPerPerson, "text", summary?.budgetPerPerson ?? demand?.budgetPerPerson),
      ]),
    },
    {
      key: "customer",
      title: customizationDemandGroupLabels.customer,
      fields: buildFields([
        field("customerType", customizationDemandFieldLabels.customerType, "text", customerRequirements?.customerType ?? project?.customerType),
        field("tripType", customizationDemandFieldLabels.tripType, "text", customerRequirements?.tripType ?? project?.tripType),
        field("signType", customizationDemandFieldLabels.signType, "text", customerRequirements?.signType ?? project?.signType),
        field(
          "schoolPickupRequirement",
          customizationDemandFieldLabels.schoolPickupRequirement,
          "text",
          customerRequirements?.schoolPickupRequirement ?? demand?.schoolPickupRequirement,
        ),
        field(
          "dispatcherUserName",
          customizationDemandFieldLabels.dispatcherUserName,
          "text",
          formatDispatcherDisplay(project),
        ),
        field("operatingCompany", customizationDemandFieldLabels.operatingCompany, "text", customerRequirements?.operatingCompany ?? project?.operatingCompany),
        field("teachingRequirements", customizationDemandFieldLabels.teachingRequirements, "summary", customerRequirements?.teachingRequirements ?? demand?.teachingRequirements),
        field("specialRequirements", customizationDemandFieldLabels.specialRequirements, "summary", customerRequirements?.specialRequirements ?? demand?.specialRequirements),
        field("invoiceRequirement", customizationDemandFieldLabels.invoiceRequirement, "summary", customerRequirements?.invoiceRequirement ?? demand?.invoiceRequirement),
        field("remark", customizationDemandFieldLabels.remark, "summary", customerRequirements?.remark ?? demand?.remark),
      ]),
    },
    {
      key: "teacher",
      title: customizationDemandGroupLabels.teacher,
      fields: buildFields([
        field("teacherMealRequirement", customizationDemandFieldLabels.teacherMealRequirement, "summary", teacherRequirements?.teacherMealRequirement ?? demand?.teacherMealRequirement),
        field("teacherTicketRequirement", customizationDemandFieldLabels.teacherTicketRequirement, "summary", teacherRequirements?.teacherTicketRequirement ?? demand?.teacherTicketRequirement),
        field("teacherTransportRequirement", customizationDemandFieldLabels.teacherTransportRequirement, "summary", teacherRequirements?.teacherTransportRequirement ?? demand?.teacherTransportRequirement),
        field("teacherAccommodationRequirement", customizationDemandFieldLabels.teacherAccommodationRequirement, "summary", teacherRequirements?.teacherAccommodationRequirement ?? demand?.teacherAccommodationRequirement),
      ]),
    },
    {
      key: "service",
      title: customizationDemandGroupLabels.service,
      fields: buildFields([
        field("guideCount", customizationDemandFieldLabels.guideCount, "number", serviceStandard?.guideCount),
        field("guideNote", customizationDemandFieldLabels.guideNote, "summary", serviceStandard?.guideNote),
        field("includeTickets", customizationDemandFieldLabels.includeTickets, "tag", buildBooleanTags(serviceStandard?.includeTickets, "包含考察点门票", "不包含考察点门票")),
        field("includeMeals", customizationDemandFieldLabels.includeMeals, "tag", buildBooleanTags(serviceStandard?.includeMeals, "全程含餐", "全程不含餐")),
        field("roundTripTransportNote", customizationDemandFieldLabels.roundTripTransportNote, "summary", serviceStandard?.roundTripTransportNote),
        field("localTransportNote", customizationDemandFieldLabels.localTransportNote, "summary", serviceStandard?.localTransportNote),
        field("accommodationNote", customizationDemandFieldLabels.accommodationNote, "summary", serviceStandard?.accommodationNote),
        field("companyVisitCount", customizationDemandFieldLabels.companyVisitCount, "number", serviceStandard?.companyVisitCount),
        field("intangibleHeritageCount", customizationDemandFieldLabels.intangibleHeritageCount, "number", serviceStandard?.intangibleHeritageCount),
        field("ticketNote", customizationDemandFieldLabels.ticketNote, "tag", buildTicketTags(serviceStandard)),
        field("mealStandards", customizationDemandFieldLabels.mealStandards, "tag", serviceStandard?.mealStandards ?? []),
        field("mealNote", customizationDemandFieldLabels.mealNote, "summary", serviceStandard?.mealNote),
        field("companyVisitNote", customizationDemandFieldLabels.companyVisitNote, "summary", serviceStandard?.companyVisitNote),
        field("intangibleHeritageNote", customizationDemandFieldLabels.intangibleHeritageNote, "summary", serviceStandard?.intangibleHeritageNote),
      ]),
    },
  ];
}

/** 将字段定义转成可渲染数据。 */
function buildFields(fields: DemandFieldSource[]): CustomizationDemandDisplayGroup["fields"] {
  return fields
    .filter((item) => !isHiddenDemandField(item.key))
    .map((item) =>
      enhanceDemandDisplayField({
        key: item.key,
        label: resolveDemandFieldLabel(item.key, item.label),
        value: normalizeDisplayText(item.key, item.displayText ?? item.value, item.kind),
        kind: item.kind,
        displayText: normalizeDisplayText(item.key, item.displayText ?? item.value, item.kind),
        hit: item.hit,
        changed: false,
        span: item.span,
      }),
    );
}

/** 将后端分组结构规范化为中文展示。 */
function normalizeDemandDisplayGroups(
  groups: CustomizationDemandDisplayGroup[],
): CustomizationDemandDisplayGroup[] {
  return groups.map((group) => ({
    ...group,
    title: customizationDemandGroupLabels[group.key] ?? group.title,
    fields: group.fields
      .filter((field) => !isHiddenDemandField(field.key))
      .map((field) =>
        enhanceDemandDisplayField({
          ...field,
          label: resolveDemandFieldLabel(field.key, field.label),
        }),
      ),
  }));
}

/** 合并后端展示分组和本地结构化兜底字段，避免后端漏 field_groups 时页面少字段。 */
function mergeDemandDisplayGroups(
  groups: CustomizationDemandDisplayGroup[],
  fallbackGroups: CustomizationDemandDisplayGroup[],
): CustomizationDemandDisplayGroup[] {
  const result = groups.map((group) => ({
    ...group,
    key: normalizeDemandGroupKey(group.key),
    fields: [...group.fields],
  }));
  const groupIndexByKey = new Map(result.map((group, index) => [group.key, index]));

  for (const fallbackGroup of fallbackGroups) {
    const normalizedKey = normalizeDemandGroupKey(fallbackGroup.key);
    const groupIndex = groupIndexByKey.get(normalizedKey);
    if (groupIndex === undefined) {
      groupIndexByKey.set(normalizedKey, result.length);
      result.push({ ...fallbackGroup, key: normalizedKey });
      continue;
    }

    const targetGroup = result[groupIndex];
    const fallbackFieldByKey = new Map(
      fallbackGroup.fields.map((field) => [normalizeDemandFieldKey(field.key), field]),
    );
    targetGroup.fields = targetGroup.fields.map((field) => {
      const fallbackField = fallbackFieldByKey.get(normalizeDemandFieldKey(field.key));
      if (fallbackField && shouldPreferStructuredRelationField(field.key, fallbackField)) {
        return fallbackField;
      }
      return field;
    });
    const fieldKeys = new Set(targetGroup.fields.map((field) => normalizeDemandFieldKey(field.key)));
    const missingFields = fallbackGroup.fields.filter(
      (field) => !fieldKeys.has(normalizeDemandFieldKey(field.key)),
    );
    if (missingFields.length > 0) {
      targetGroup.fields = [...targetGroup.fields, ...missingFields];
    }
  }

  return result;
}

/** 统一 PC 展示分组内字段顺序，基础信息跟手机端保持一致。 */
function reorderDemandDisplayGroups(
  groups: CustomizationDemandDisplayGroup[],
): CustomizationDemandDisplayGroup[] {
  return groups.map((group) => {
    if (normalizeDemandGroupKey(group.key) !== "base") {
      return group;
    }
    return {
      ...group,
      fields: [...group.fields].sort(compareBaseDemandDisplayFields),
    };
  });
}

/** 基础信息字段排序：跟手机端一致，计调不放入基础信息。 */
function compareBaseDemandDisplayFields(
  previous: CustomizationDemandDisplayField,
  next: CustomizationDemandDisplayField,
): number {
  return getBaseDemandDisplayFieldOrder(previous.key) - getBaseDemandDisplayFieldOrder(next.key);
}

/** 获取基础信息字段排序权重。 */
function getBaseDemandDisplayFieldOrder(fieldKey: string): number {
  return baseDemandDisplayFieldOrder.get(normalizeDemandFieldKey(fieldKey)) ?? Number.MAX_SAFE_INTEGER;
}

/** 关系字段以最新结构化关系为准，旧展示分组只保留未知字段。 */
function shouldPreferStructuredRelationField(
  fieldKey: string,
  fallbackField: CustomizationDemandDisplayField,
): boolean {
  return relationDisplayFieldKeys.has(normalizeDemandFieldKey(fieldKey)) && hasDisplayFieldValue(fallbackField);
}

/** 判断当前需求是否已有结构化关系值。 */
function hasStructuredRelationValue(
  demand: CustomizationDemandDraft | null,
  fieldKey: string,
): boolean {
  const relations = demand?.relations;
  if (!relations) {
    return false;
  }
  switch (normalizeDemandFieldKey(fieldKey)) {
    case "provinces":
      return hasConcreteValue(relations.provinces);
    case "majorNames":
      return hasConcreteValue(relations.majorNames);
    case "pointNames":
      return hasConcreteValue(relations.pointNames);
    case "stayAreaNames":
      return hasConcreteValue(relations.stayAreaNames);
    default:
      return false;
  }
}

/** 统一展示字段 key，兼容后端蛇形字段名。 */
function normalizeDemandFieldKey(key: string): string {
  const normalizedKey = toCamelCase(key.trim());
  return normalizedKey === "provinceNames" ? "provinces" : normalizedKey;
}

/** 判断展示字段是否有非占位内容。 */
function hasDisplayFieldValue(field: CustomizationDemandDisplayField): boolean {
  const text = String(field.displayText ?? field.value ?? "").trim();
  return Boolean(text && text !== "--");
}

/** 兼容后端可能返回的明细分组蛇形 key。 */
function normalizeDemandGroupKey(key: string): string {
  const normalizedKey = toCamelCase(key.trim());
  if (normalizedKey === "customerRequirements") {
    return "customer";
  }
  if (normalizedKey === "teacherRequirements") {
    return "teacher";
  }
  if (normalizedKey === "serviceStandard") {
    return "service";
  }
  return normalizedKey;
}

/** 补齐需求字段的展示样式。 */
function enhanceDemandDisplayField(field: CustomizationDemandDisplayField): CustomizationDemandDisplayField {
  const kind = resolveDemandFieldKind(field.kind);
  const displayText = normalizeDisplayText(field.key, field.displayText ?? field.value, kind);

  return {
    ...field,
    kind,
    value: displayText,
    displayText,
    span: resolveDemandFieldSpan(field.key, kind, field.span),
  };
}

/** 解析后端字段类型，缺省按文本处理。 */
function resolveDemandFieldKind(kind: string | undefined): DemandFieldKind {
  if (
    kind === "text" ||
    kind === "tag" ||
    kind === "number" ||
    kind === "datetime" ||
    kind === "summary"
  ) {
    return kind;
  }
  return "text";
}

const hiddenDemandFieldKeys = new Set(["destinationSummary", "totalCount"]);
const baseDemandDisplayFieldOrder = new Map(
  [
    "primarySchoolNameSnapshot",
    "customerName",
    "contactName",
    "majorNames",
    "departureStartDate",
    "durationDays",
    "studentCount",
    "teacherCount",
    "budgetPerPerson",
    "provinces",
    "stayAreaNames",
    "pointNames",
  ].map((key, index) => [key, index] as const),
);
const wideDemandFieldKeys = new Set([
  "provinces",
  "majorNames",
  "pointNames",
  "stayAreaNames",
  "teachingRequirements",
  "specialRequirements",
  "invoiceRequirement",
  "remark",
  "teacherMealRequirement",
  "teacherTicketRequirement",
  "teacherTransportRequirement",
  "teacherAccommodationRequirement",
  "roundTripTransportNote",
  "localTransportNote",
  "accommodationNote",
  "ticketNote",
  "mealStandards",
  "mealNote",
  "companyVisitNote",
  "intangibleHeritageNote",
]);

/** 判断需求字段是否应隐藏。 */
function isHiddenDemandField(key: string): boolean {
  return hiddenDemandFieldKeys.has(key);
}

/** 判断必填项状态中的派生字段是否应隐藏。 */
function isDerivedMissingDemandField(field: string): boolean {
  return field === "总人数" || field === "totalCount" || field === "total_count";
}

/** 判断后端复核原因是否是固定必填字段标签。 */
function isRequiredMissingDemandField(field: string): boolean {
  return requiredDemandFieldLabels.has(field);
}

/** 判断必填缺项是否属于目的地组合。 */
function isDestinationRequiredMissingField(field: string): boolean {
  return field === destinationRequiredFieldLabel || destinationRequiredFieldLabels.has(field);
}

/** 判断当前需求是否已有任一目的地组合值。 */
function hasDemandDestinationValue(demand: CustomizationDemandDraft): boolean {
  const relations = demand.relations;
  return Boolean(
    relations &&
      (hasConcreteValue(relations.provinces) ||
        hasConcreteValue(relations.pointNames) ||
        hasConcreteValue(relations.stayAreaNames)),
  );
}

/** 推断需求字段跨列宽度。 */
function resolveDemandFieldSpan(
  key: string,
  kind: DemandFieldKind,
  currentSpan?: number,
): number | undefined {
  if (wideDemandFieldKeys.has(key) || kind === "summary") {
    return 3;
  }
  return currentSpan ?? 1;
}

type DemandFieldSource = {
  key: string;
  label: string;
  kind: DemandFieldKind;
  value: unknown;
  displayText?: string;
  hit?: string[];
  span?: number;
};

/** 定义单个字段。 */
function field(
  key: string,
  label: string,
  kind: DemandFieldKind,
  value: unknown,
  extra?: Pick<DemandFieldSource, "displayText" | "hit" | "span">,
): DemandFieldSource {
  return { key, label, kind, value, ...extra };
}

/** 解析字段展示名称。 */
function resolveDemandFieldLabel(key: string, label: string): string {
  const mappedLabel = customizationDemandFieldLabels[key];
  if (mappedLabel) {
    return mappedLabel;
  }
  const normalizedLabel = label.trim();
  if (!normalizedLabel) {
    return key;
  }
  return normalizedLabel;
}

/** 解析字段展示值。 */
function normalizeDemandFieldValue(key: string, value: unknown, kind: DemandFieldKind): string {
  if (value === null || value === undefined || value === "") {
    return "--";
  }
  if (Array.isArray(value)) {
    return value.map((item) => normalizeDemandFieldValue(key, item, kind)).filter((item) => item !== "--").join(" / ") || "--";
  }
  if (typeof value === "boolean") {
    return value ? "是" : "否";
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  const text = String(value).trim();
  if (!text) {
    return "--";
  }
  const lookupMap = customizationDemandLookupValueLabels[key];
  const mappedText = lookupMap?.[text];
  if (mappedText) {
    return mappedText;
  }
  if (lookupMap && looksLikeRawLookupValue(text)) {
    return "未命名";
  }
  return text;
}

/** 规范化字段展示文本。 */
function normalizeDisplayText(key: string, value: unknown, kind: DemandFieldKind): string {
  if (kind === "summary") {
    return formatFieldValue(value, kind, key);
  }
  return normalizeDemandFieldValue(key, value, kind);
}

/** 构建门票标签。 */
function buildTicketTags(serviceStandard: CustomizationServiceStandardDraft | undefined): string[] {
  const tags: string[] = [];
  const note = serviceStandard?.ticketNote?.trim();
  if (note) {
    tags.push(note);
  }
  return tags;
}

/** 构建是否类标签。 */
function buildBooleanTags(value: boolean | undefined, yesLabel: string, noLabel: string): string[] {
  if (value === true) {
    return [yesLabel];
  }
  if (value === false) {
    return [noLabel];
  }
  return [];
}

/** 转换 changedFields 的旧值/新值为文本。 */
function formatChangeValue(value: unknown, key?: string): string {
  if (value === null || value === undefined || value === "") {
    return "--";
  }
  if (Array.isArray(value)) {
    return (
      value.map((item) => formatChangeValue(item, key)).filter((item) => item !== "--").join(" / ") ||
      "--"
    );
  }
  if (typeof value === "boolean") {
    return value ? "是" : "否";
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if ("previous" in record || "current" in record) {
      return `${formatChangeValue(record.previous, key)} -> ${formatChangeValue(record.current, key)}`;
    }
    return JSON.stringify(value);
  }
  const text = String(value).trim();
  if (!text) {
    return "--";
  }
  if (!key) {
    return text;
  }
  const lookupMap = customizationDemandLookupValueLabels[key];
  return lookupMap?.[text] ?? text;
}

/** 判断变更项是否有真实内容。 */
function hasMeaningfulChange(previous: unknown, current: unknown): boolean {
  return hasConcreteValue(previous) || hasConcreteValue(current);
}

/** 判断值是否有实际内容。 */
function hasConcreteValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((item) => hasConcreteValue(item));
  }
  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }
  return true;
}

/** 格式化字段值。 */
function formatFieldValue(value: unknown, kind: DemandFieldKind, key?: string): string {
  if (value === null || value === undefined || value === "") {
    return "--";
  }
  if (kind === "tag") {
    if (Array.isArray(value)) {
      return value
        .map((item) => normalizeDemandFieldValue(key ?? "", item, kind))
        .filter((item) => item !== "--")
        .join(" / ") || "--";
    }
    return normalizeDemandFieldValue(key ?? "", value, kind);
  }
  if (kind === "summary") {
    return String(value).trim() || "--";
  }
  if (typeof value === "boolean") {
    return value ? "是" : "否";
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeDemandFieldValue(key ?? "", item, kind))
      .filter((item) => item !== "--")
      .join(" / ") || "--";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return normalizeDemandFieldValue(key ?? "", value, kind);
}

/** 判断值是否像数据库查找字段的代码值。 */
function looksLikeRawLookupValue(value: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(value) || /^[A-Z0-9_]+$/.test(value);
}

/** 清理可选字符串。 */
function normalizeOptionalText(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const text = String(value).trim();
  return text || undefined;
}

/** 展示第一计调姓名与电话，不改变编辑提交值。 */
function formatDispatcherDisplay(project?: CustomizationProjectSnapshot | null): string | undefined {
  const name = normalizeOptionalText(project?.dispatcherUserName);
  const phone = normalizeOptionalText(project?.dispatcherUserPhone);
  return [name, phone].filter(Boolean).join(" / ") || undefined;
}

/** 合计学生与老师人数。 */
function sumCounts(...values: Array<number | undefined>): number | undefined {
  const total = values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
  return total > 0 ? total : undefined;
}
